import React from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../i18n/LanguageContext";
import { ArrowRight, Users, Briefcase } from "lucide-react";

const B2BvsB2CPage = () => {
  const { t, lang, setLang } = useLanguage();
  const s = t.stage;

  return (
    <div data-testid="stage-page" className="min-h-screen w-full flex flex-col bg-[#FDFDFD] text-[#111] relative overflow-hidden">
      {/* minimal header with logo + lang switcher */}
      <div className="absolute top-0 left-0 right-0 z-20 flex items-center justify-center px-6 md:px-12 lg:px-20 py-6">
        <img
          data-testid="stage-brand"
          src="/assets/green-horizon-logo-transparent.png"
          alt="Green Horizon Life — Enrico Müller & Colleen Schmidt"
          className="h-24 md:h-28 lg:h-32 w-auto"
        />
        <div data-testid="stage-lang-switcher" className="absolute right-6 md:right-12 lg:right-20 top-1/2 -translate-y-1/2 flex items-center text-[0.7rem] uppercase tracking-[0.24em] border border-neutral-300 rounded-full overflow-hidden bg-white/70 backdrop-blur">
          <button data-testid="stage-lang-de" onClick={() => setLang("de")} className={`px-3 py-1.5 ${lang === "de" ? "text-black font-semibold" : "text-neutral-400 hover:text-black"} transition`}>DE</button>
          <span className="w-px h-3 bg-neutral-300" />
          <button data-testid="stage-lang-en" onClick={() => setLang("en")} className={`px-3 py-1.5 ${lang === "en" ? "text-black font-semibold" : "text-neutral-400 hover:text-black"} transition`}>EN</button>
        </div>
      </div>

      {/* Central question with both names + duo portrait */}
      <div className="w-full flex flex-col items-center justify-center text-center px-6 pt-32 md:pt-36 lg:pt-40 pb-10 max-w-4xl mx-auto fade-up">
        <div className="relative w-32 h-32 md:w-40 md:h-40 lg:w-44 lg:h-44 rounded-full overflow-hidden ring-4 ring-white shadow-xl mb-7">
          <img
            data-testid="stage-duo-portrait"
            src="/assets/duo_portrait.png"
            alt="Enrico Müller & Colleen Schmidt"
            className="w-full h-full object-cover"
            style={{ objectPosition: "center 28%" }}
          />
        </div>
        <span className="eyebrow text-neutral-500">{s.eyebrow}</span>
        <h1 className="entry-display mt-6 text-3xl md:text-5xl lg:text-6xl leading-[1.05] text-[#111]">
          {s.question}
        </h1>
        <p className="mt-5 text-base md:text-lg text-neutral-500 max-w-2xl">
          {s.lead}
        </p>
      </div>

      {/* Split-screen B2C / B2B */}
      <div className="relative flex-1 flex flex-col md:flex-row min-h-[58vh] md:min-h-[62vh] border-t border-neutral-200">
        {/* LEFT — Privat (B2C) */}
        <Link
          to="/privat"
          data-testid="stage-privat-button"
          className="split-side relative flex flex-col justify-between p-10 md:p-16 lg:p-24 group bg-[#F4F1ED] hover:bg-[#EDE7DF] transition-colors overflow-hidden"
        >
          <div
            className="absolute inset-0 bg-cover bg-center opacity-[0.30] group-hover:opacity-[0.40] transition-opacity duration-700"
            style={{
              backgroundImage:
                "url('https://images.unsplash.com/photo-1499209974431-9dddcece7f88?auto=format&fit=crop&w=1600&q=80')",
              filter: "saturate(0.82) sepia(0.10)",
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#F4F1ED]/65 via-[#F4F1ED]/35 to-[#F4F1ED]/20" />
          <div className="relative z-10">
            <Users size={32} className="text-[#738678] mb-4" />
            <span className="eyebrow text-[#5C6660]">{s.privat.eyebrow}</span>
            <h2 className="mt-6 text-4xl md:text-5xl lg:text-6xl leading-tight text-[#2C332E]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 400 }}>
              {s.privat.title}<br />
              <span className="italic text-[#738678]">{s.privat.subtitle}</span>
            </h2>
            <p className="mt-5 text-[#5C6660] max-w-md leading-relaxed">{s.privat.body}</p>
          </div>
          <div className="relative z-10 mt-12 flex items-center justify-between">
            <span className="inline-flex items-center gap-3 text-[#2C332E] text-sm uppercase tracking-[0.24em]">
              {s.privat.action}
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-2" />
            </span>
            <span className="text-5xl md:text-7xl text-[#D9C5B2]/80 leading-none" style={{ fontFamily: 'Cormorant Garamond, serif' }}>I</span>
          </div>
          <div className="grain" />
        </Link>

        <div className="entry-divider" />

        {/* RIGHT — Business (B2B) */}
        <Link
          to="/business"
          data-testid="stage-business-button"
          className="split-side relative flex flex-col justify-between p-10 md:p-16 lg:p-24 group bg-[#1A2620] hover:bg-[#20302A] transition-colors text-white overflow-hidden"
        >
          <div
            className="absolute inset-0 bg-cover bg-center opacity-[0.32] group-hover:opacity-[0.42] transition-opacity duration-700"
            style={{
              backgroundImage:
                "url('https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1600&q=80')",
              filter: "saturate(0.65) contrast(1.05) brightness(0.92)",
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-l from-[#1A2620]/70 via-[#1A2620]/40 to-[#1A2620]/20" />
          <div className="relative z-10">
            <Briefcase size={32} className="text-[#A8BCA6] mb-4" />
            <span className="eyebrow text-[#A8BCA6]">{s.business.eyebrow}</span>
            <h2 className="mt-6 text-4xl md:text-5xl lg:text-6xl leading-tight tracking-tight" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>
              {s.business.title}<br />
              <span className="italic text-[#A8BCA6]">{s.business.subtitle}</span>
            </h2>
            <p className="mt-5 text-[#C8D5C6] max-w-md leading-relaxed">{s.business.body}</p>
          </div>
          <div className="relative z-10 mt-12 flex items-center justify-between">
            <span className="inline-flex items-center gap-3 text-white text-[0.78rem] uppercase tracking-[0.28em]">
              {s.business.action}
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-2" />
            </span>
            <span className="text-5xl md:text-7xl text-[#A8BCA6]/60 leading-none" style={{ fontFamily: 'Cormorant Garamond, serif' }}>II</span>
          </div>
          <div className="grain" />
        </Link>
      </div>

      <div className="text-center text-[0.72rem] uppercase tracking-[0.28em] text-neutral-400 py-6">
        {s.footer}
      </div>
    </div>
  );
};

export default B2BvsB2CPage;
